/**
 * Helpers puros da área de Solicitações do colaborador.
 * Sem efeito colateral — tudo que toca storage vive em rascunho.js.
 */
import { TIPOS_SOLICITACAO } from "../../../services/solicitacoes";

let sequenciaUid = 0;

export function gerarUid() {
  sequenciaUid += 1;
  return `${Date.now().toString(36)}-${sequenciaUid}`;
}

/** Respostas podem vir cruas ou embrulhadas em `{ data }`. */
export function unwrapItem(payload) {
  return payload?.data ?? payload ?? null;
}

/** Listas podem vir como array puro OU `{ results: [] }` (paginado DRF). */
export function unwrapLista(payload) {
  const data = unwrapItem(payload);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

/** "77,8" → 77.8 ; vazio/inválido → null. */
export function parseDecimal(valor) {
  if (valor === null || valor === undefined) return null;
  const texto = String(valor).trim().replace(",", ".");
  if (!texto) return null;
  const numero = Number(texto);
  return Number.isFinite(numero) ? numero : null;
}

export function tipoPorValor(valor) {
  return TIPOS_SOLICITACAO.find((t) => t.value === valor) || null;
}

/**
 * `registro` continua igualzinho ao molde recém-criado (nada digitado)?
 *
 * Usado para tornar reversível o clique acidental em "Adicionar outra peça" /
 * "Adicionar outro item": uma peça intocada pode ser descartada sem perguntar
 * nada, porque não há o que perder. Compara contra o próprio molde (`novaPeca`
 * / `novoItem`) em vez de uma lista de campos — assim campo novo no formulário
 * entra na conta sozinho, sem alguém precisar lembrar de atualizar aqui.
 */
export function registroIntocado(registro, molde) {
  if (!registro || !molde) return false;
  return Object.keys(molde).every((campo) => {
    if (campo === "uid") return true; // sempre difere, por construção
    const atual = registro[campo];
    if (Array.isArray(atual)) return atual.length === 0; // fotos
    return String(atual ?? "") === String(molde[campo] ?? "");
  });
}

/**
 * Limites físicos do corte MDF — espelhados no backend (solicitacoes/models.py):
 * a chapa útil tem 2730×1830 mm e as máquinas não cortam peça menor que 20 mm.
 */
export const MEDIDA_MIN_MM = 20;
export const COMPRIMENTO_MAX_MM = 2730;
export const LARGURA_MAX_MM = 1830;

/**
 * Motivo "Outro" não informa nada a quem lê a solicitação depois — quem
 * escolhe tem que escrever o motivo real. Espelhado no backend
 * (solicitacoes/models.py + services.py), que rejeita o mesmo caso: a tela
 * pode ser burlada, a API não. Vale para as duas famílias — motivo_categoria
 * da peça (ASM/AST/ASP) e motivo do item de RQA; os dois catálogos têm "OUTRO".
 */
export const MOTIVO_OUTRO = "OUTRO";
export const MOTIVO_DETALHE_MIN = 7;

/** Mensagem de erro do detalhe do motivo, ou "" quando está válido. */
export function erroDetalheDoMotivo(motivo, detalhe) {
  if (motivo !== MOTIVO_OUTRO) return "";
  if (String(detalhe || "").trim().length >= MOTIVO_DETALHE_MIN) return "";
  return `Descreva o motivo real (mínimo ${MOTIVO_DETALHE_MIN} caracteres).`;
}

/**
 * Valida comprimento/largura contra os limites do corte. Devolve a mensagem
 * de erro (pronta para exibir) ou null. Campo vazio passa — obrigatoriedade
 * é tratada à parte (backend exige medidas na peça nova).
 */
export function validarMedidaMdf(campo, valor) {
  const v = parseDecimal(valor);
  if (v == null || v <= 0) return null;
  const ehComprimento = campo === "comprimento_mm";
  const max = ehComprimento ? COMPRIMENTO_MAX_MM : LARGURA_MAX_MM;
  if (v > max) {
    return (
      `${ehComprimento ? "Comprimento" : "Largura"} de ${numeroEnxuto(v)} mm ` +
      `é maior que a chapa de MDF — o máximo é ${max} mm.`
    );
  }
  if (v < MEDIDA_MIN_MM) {
    // Exemplo curto de propósito: precisa caber nos 26 chars da observação.
    const exemplo = ehComprimento ? "compr. final" : "larg. final";
    return (
      `Medida mínima: ${MEDIDA_MIN_MM} mm — é o menor corte que as máquinas ` +
      `conseguem fazer. Solicite a peça com ${MEDIDA_MIN_MM} mm e escreva no ` +
      `campo Observação a redução desejada, com a medida final e se é no ` +
      `comprimento ou na largura (ex.: "${exemplo} ${numeroEnxuto(v)}mm").`
    );
  }
  return null;
}

export function formatarDataRelativa(iso) {
  if (!iso) return "";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "";
  const minutos = Math.floor((Date.now() - data.getTime()) / 60000);
  if (minutos < 1) return "agora";
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias === 1) return "ontem";
  if (dias < 7) return `há ${dias} dias`;
  return data.toLocaleDateString("pt-BR");
}

export function formatarDataHora(iso) {
  if (!iso) return "";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "";
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "C 778 (veio) × L 649 × E 18 mm" — só com as medidas presentes. */
export function formatarMedidas(peca) {
  const partes = [];
  if (peca?.comprimento_mm) {
    partes.push(`C ${peca.comprimento_mm}${peca.sentido_veio ? " (veio)" : ""}`);
  }
  if (peca?.largura_mm) partes.push(`L ${peca.largura_mm}`);
  if (peca?.espessura_mm) partes.push(`E ${peca.espessura_mm}`);
  return partes.length ? `${partes.join(" × ")} mm` : "";
}

/** "18" (inteiro) ou "18,5" — número enxuto, sem casas à toa. */
function numeroEnxuto(n) {
  return Number.isInteger(n) ? String(n) : n.toLocaleString("pt-BR");
}

/**
 * Nome da cor do MDF pronto para exibir. O cadastro real já embute a espessura
 * no nome ("ARAUCO 15 mm", "ARENITO 18mm"), então NÃO anexamos de novo — só
 * complementamos quando o nome não traz nenhum token de espessura (dado legado
 * como o fixture "MDF Branco"). Evita o "ARAUCO 15 mm 15 mm".
 */
export function formatarCorMdf(nome, espessura) {
  if (!nome) return "";
  const texto = String(nome).trim();
  if (/\d\s*mm\b/i.test(texto)) return texto; // já tem "15 mm" / "18mm" — não duplicar
  const e = parseDecimal(espessura);
  return e != null ? `${texto} ${numeroEnxuto(e)} mm` : texto;
}

/** "778x649x18" — comprimento × largura × espessura, compacto e sem unidade. */
export function formatarMedidaCompacta(peca) {
  const c = parseDecimal(peca?.comprimento_mm);
  const l = parseDecimal(peca?.largura_mm);
  const e = parseDecimal(peca?.espessura_mm);
  if (c == null || l == null) return "";
  return `${numeroEnxuto(c)}x${numeroEnxuto(l)}${e != null ? `x${numeroEnxuto(e)}` : ""}`;
}

export const BORDAS_FITA = [
  { campo: "fita_traseira", label: "Traseira" },
  { campo: "fita_frente", label: "Frente" },
  { campo: "fita_le", label: "Lado esquerdo" },
  { campo: "fita_ld", label: "Lado direito" },
];

/**
 * FONTE ÚNICA do mapa borda física → campo de fita (REGRA DO VEIO — não
 * inverter; ver docstring de FitaBordaSimulador e solicitacoes/xls.py).
 * Comprimento (veio) é o eixo VERTICAL:
 *   - bordas VERTICAIS (esq/dir) correm no comprimento → Frente / Traseira
 *   - bordas HORIZONTAIS (topo/base) correm na largura → LE / LD
 * O simulador interativo e a visualização read-only (carrossel do detalhe)
 * consomem esta lista para nunca divergirem.
 */
export const LADOS_PECA = [
  // LE em baixo / LD em cima (inversão pedida em 2026-07-07 — só LE↔LD).
  { lado: "topo", campo: "fita_ld", rotulo: "LD", orientacao: "h" },
  { lado: "base", campo: "fita_le", rotulo: "LE", orientacao: "h" },
  { lado: "esq", campo: "fita_frente", rotulo: "Frente", orientacao: "v" },
  { lado: "dir", campo: "fita_traseira", rotulo: "Traseira", orientacao: "v" },
];

export function contarBordasComFita(peca) {
  return BORDAS_FITA.reduce((total, b) => total + (peca?.[b.campo] ? 1 : 0), 0);
}

/**
 * Só a COR da fita para exibição. O cadastro embute largura-espessura-cor no
 * código ("22-0.4-Branco TX"); aqui devolvemos apenas "Branco TX". Robusto:
 * dado novo (já a cor) volta igual; dado antigo (código) tem o prefixo tirado.
 */
export function corDaFita(fitaNome) {
  if (!fitaNome) return "";
  const s = String(fitaNome).trim();
  // "{largura}-{espessura}{sep}{cor}" — separador traço ("19-0.4-Azul") ou
  // espaço ("22-0.4 Bambu"). Espelha o parse_codigo_fita do backend:
  const m = s.match(/^\d+(?:[.,]\d+)?-\d+(?:[.,]\d+)?(?:[\s-]+(.*))?$/);
  if (!m) return s; // não é código: o texto já é a cor
  return m[1] ? m[1].trim() : ""; // é código: a cor (vazia se "22-0.4" sem cor)
}

export function descreverFitas(peca) {
  const nomes = BORDAS_FITA.filter((b) => peca?.[b.campo]).map((b) => b.label);
  if (!nomes.length) return "";
  const cor = corDaFita(peca?.fita_nome);
  return cor ? `${nomes.join(", ")} — ${cor}` : nomes.join(", ");
}
